import { BILLING_CATEGORIES, BILLING_PERIODS, type BillingPeriod } from "@/lib/billing";

/**
 * URL クエリに載せる課金ダッシュボードの絞り込み。組織は ID だけを載せ、表示名は
 * 組織一覧の取得後に補う (名前を URL に置くと改名で古い名前が残るため)。
 */
export interface BillingFilterUrlState {
	period: BillingPeriod;
	categoryIds: string[];
	organizationId: string;
	includeArchived: boolean;
}

/**
 * URL クエリから復元した絞り込み。`includeArchived` は localStorage にも独立して
 * 永続化されているため (`useBillingFilterStore`)、URL に `archived` が無い場合は
 * 「既定の true」ではなく undefined を返し、呼び出し側が保存済みの好みを保てるようにする。
 */
export type ParsedBillingFilterState = Omit<BillingFilterUrlState, "includeArchived"> & {
	includeArchived: boolean | undefined;
};

const PERIOD_IDS: ReadonlySet<string> = new Set(BILLING_PERIODS.map((p) => p.id));
const CATEGORY_IDS: ReadonlySet<string> = new Set(BILLING_CATEGORIES.map((c) => c.id));

/**
 * URL クエリから絞り込みを復元する。`period` は常に書き出すので、これが無い (または
 * 未知の値) URL は課金フィルタを持たないとみなして null を返し、呼び出し側は手元の
 * 選択を保つ。値がある場合は URL を完全な状態として扱い、`categoryIds`/`organizationId`
 * は省略時に既定 (全カテゴリ・自組織) と解釈する。`includeArchived` だけは省略時に
 * undefined を返し、呼び出し側が保存済みの好みを上書きしないようにする。
 */
export function parseBillingFilterParams(params: URLSearchParams): ParsedBillingFilterState | null {
	const period = params.get("period");
	if (period === null || !PERIOD_IDS.has(period)) return null;
	const categoryIds = (params.get("categories") ?? "")
		.split(",")
		.filter((id) => CATEGORY_IDS.has(id));
	return {
		period: period as BillingPeriod,
		categoryIds: [...new Set(categoryIds)],
		organizationId: params.get("org") ?? "",
		includeArchived: params.has("archived") ? params.get("archived") !== "false" : undefined,
	};
}

/**
 * 絞り込みを URL クエリへ反映した新しい URLSearchParams を返す。既定値は書かず、
 * 既定へ戻したときはパラメータごと消して URL を短く保つ。
 */
export function applyBillingFilterParams(
	params: URLSearchParams,
	state: BillingFilterUrlState,
): URLSearchParams {
	const next = new URLSearchParams(params);
	next.set("period", state.period);
	if (state.categoryIds.length > 0) next.set("categories", state.categoryIds.join(","));
	else next.delete("categories");
	if (state.organizationId) next.set("org", state.organizationId);
	else next.delete("org");
	if (state.includeArchived) next.delete("archived");
	else next.set("archived", "false");
	return next;
}
